// ===================================================================
// SHELTER MANAGEMENT — Module Layout with grouped sub-navigation
// ===================================================================

import { Outlet, useLocation } from 'react-router-dom';
import {
  IconLayoutDashboard, IconBuildingCommunity, IconUserPlus, IconUsers,
  IconUsersGroup, IconQrcode, IconStethoscope, IconTransfer,
  IconPackage, IconDog, IconCar, IconAlertTriangle,
  IconExternalLink, IconChartBar, IconMapPin, IconReport,
  IconSpeakerphone, IconClipboardList, IconBed, IconHeart, IconLibrary,
} from '@tabler/icons-react';
import { useFilteredNav } from '../../hooks/useFilteredNav';
import type { NavItem } from '../../types/nav';
import ModuleNav from '../../components/layout/ModuleNav';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard',      path: '/shelter',               icon: IconLayoutDashboard, exact: true },
  { label: 'Shelters',       path: '/shelter/shelters',      icon: IconBuildingCommunity },
  { label: 'Intake',         path: '/shelter/intake',        icon: IconUserPlus },
  { label: 'Survivors',      path: '/shelter/survivors',     icon: IconUsers },
  { label: 'Families',       path: '/shelter/families',      icon: IconUsersGroup },
  { label: 'Pets',           path: '/shelter/pets',          icon: IconDog },
  { label: 'Vehicles',       path: '/shelter/vehicles',      icon: IconCar },
  { label: 'Lookup',         path: '/shelter/lookup',        icon: IconQrcode },
  { label: 'Medical',        path: '/shelter/medical',       icon: IconStethoscope, minLevel: 'editor' },
  { label: 'Transfers',      path: '/shelter/transfers',     icon: IconTransfer },
  { label: 'Property',       path: '/shelter/property',      icon: IconPackage },
  { label: 'Supplies',       path: '/shelter/supplies',      icon: IconBed },
  { label: 'Distribution',   path: '/shelter/distribution',  icon: IconPackage },
  { label: 'Triage Setup',   path: '/shelter/triage',        icon: IconClipboardList, minLevel: 'editor' },
  { label: 'Incidents',      path: '/shelter/incidents',     icon: IconAlertTriangle },
  { label: 'Referrals',      path: '/shelter/referrals',     icon: IconExternalLink },
  { label: 'Reunification',  path: '/shelter/reunification', icon: IconHeart },
  { label: 'Comms',          path: '/shelter/comms',         icon: IconSpeakerphone, minLevel: 'editor' },
  { label: 'Map',            path: '/shelter/map',           icon: IconMapPin },
  { label: 'Reports',        path: '/shelter/reports',       icon: IconReport },
  { label: 'Facility Library', path: '/shelter/facility-library', icon: IconLibrary, minLevel: 'editor' },
  { label: 'Analytics',      path: '/shelter/analytics',     icon: IconChartBar },
];

// Group nav items by section for organized display
const NAV_GROUPS: { label: string; paths: string[] }[] = [
  { label: '', paths: ['/shelter', '/shelter/shelters'] },
  { label: 'People', paths: ['/shelter/intake', '/shelter/survivors', '/shelter/families', '/shelter/lookup'] },
  { label: 'Assets', paths: ['/shelter/pets', '/shelter/vehicles', '/shelter/property', '/shelter/supplies', '/shelter/distribution'] },
  { label: 'Operations', paths: ['/shelter/medical', '/shelter/transfers', '/shelter/triage', '/shelter/incidents', '/shelter/referrals', '/shelter/reunification'] },
  { label: 'Admin', paths: ['/shelter/comms', '/shelter/map', '/shelter/facility-library', '/shelter/reports', '/shelter/analytics'] },
];

export default function ShelterLayout() {
  const { pathname } = useLocation();
  const visibleItems = useFilteredNav('shelter', NAV_ITEMS);

  // Build visible items per group
  const groups = NAV_GROUPS.map(g => ({
    ...g,
    items: g.paths
      .map(p => visibleItems.find(vi => vi.path === p))
      .filter(Boolean) as typeof visibleItems,
  })).filter(g => g.items.length > 0);

  const currentPage = NAV_ITEMS.find(item =>
    item.exact ? pathname === item.path : pathname.startsWith(item.path)
  );

  return (
    <div style={{ padding: '24px' }}>
      <Breadcrumbs
        items={[
          { label: 'Hub', to: '/' },
          { label: 'Shelter', to: '/shelter' },
          ...(currentPage && currentPage.path !== '/shelter' ? [{ label: currentPage.label }] : []),
        ]}
      />
      <ModuleNav items={visibleItems} groups={groups} />
      <Outlet />
    </div>
  );
}
